import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import SideNav from './SideNav';
import Footer from './Footer';
import PageHeader from './PageHeader';
import BusinessScenarioModal from './BusinessScenarioModal';
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import './Common.css';
import './BusinessScenario.css';

const LOCAL_LABELS = [
  { id: 1, label_name: 'Client Interaction' },
  { id: 2, label_name: 'Technical Discussion' },
  { id: 3, label_name: 'Internal Collaboration' },
  { id: 4, label_name: 'Professional Development' }
];

const LOCAL_PROMPTS = [
  { id: 1, label_id: 1, label_name: 'Client Interaction', prompt_title: 'Help me do some Small Talk with a German client.' },
  { id: 2, label_id: 2, label_name: 'Technical Discussion', prompt_title: 'Change Request Discussion' },
  { id: 3, label_id: 3, label_name: 'Internal Collaboration', prompt_title: 'Status Update Call with Onshore/Offshore Team' },
  { id: 4, label_id: 4, label_name: 'Professional Development', prompt_title: 'Internal Job Interview / Role Transition Discussion' },
  { id: 5, label_id: 1, label_name: 'Client Interaction', prompt_title: 'Discuss project timeline with a client.' },
  { id: 6, label_id: 2, label_name: 'Technical Discussion', prompt_title: 'Delivering a Sales Presentation' },
  { id: 7, label_id: 3, label_name: 'Internal Collaboration', prompt_title: 'Lead a project kickoff meeting in German.' },
  { id: 8, label_id: 4, label_name: 'Professional Development', prompt_title: 'Write a formal complaint email to a vendor.' }
];

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const CARDS_PER_PAGE = 4;

const BusinessScenario = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language: initLang = 'german', proficiencyLevel: initLevel = 'beginner' } = location.state || {};
  const [language, setLanguage] = useState(initLang);
  const [proficiencyLevel, setProficiencyLevel] = useState(initLevel);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [labels, setLabels] = useState(LOCAL_LABELS);
  const [prompts, setPrompts] = useState(LOCAL_PROMPTS);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [expandAll, setExpandAll] = useState(false);
  const [pendingLabels, setPendingLabels] = useState([]);
  const [pendingFavorites, setPendingFavorites] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [tooltipPos, setTooltipPos] = useState(null);
  const [favRefresh, setFavRefresh] = useState(0);
  const filterRef = useRef(null);

  const userId = user?.email || user?.id || 'local-user';
  usePageTimeTracker('practice-business-scenario', user);

  const breadcrumbs = [
    { label: 'Home', path: '/', current: false },
    { label: 'Explore Learning', path: '/explore-learning', current: false },
    { label: 'Practice : Business Scenario', current: true }
  ];

  // Fetch labels
  useEffect(() => {
    if (isLocal) return;
    axios.get('/lb-api/business-scenario/labels', { withCredentials: true })
      .then(res => { if (res.data.success && res.data.labels.length) setLabels(res.data.labels); })
      .catch(err => console.error('Failed to fetch labels:', err));
  }, []);

  // Fetch all prompts (filter client-side)
  useEffect(() => {
    if (isLocal) return;
    const params = {};
    if (searchQuery.trim()) params.search = searchQuery.trim();
    axios.get('/lb-api/business-scenario/prompts', { params, withCredentials: true })
      .then(res => { if (res.data.success) { setPrompts(res.data.prompts); setCurrentPage(0); } })
      .catch(err => console.error('Failed to fetch prompts:', err));
  }, [searchQuery]);

  // Fetch favorites
  useEffect(() => {
    if (isLocal) return;
    axios.get('/lb-api/business-scenario/favorites', { params: { enterprise_id: userId, _t: Date.now() }, withCredentials: true })
      .then(res => {
        if (res.data.success) {
          setFavoriteIds(new Set(res.data.favorites.map(f => f.prompt_id)));
        }
      })
      .catch(err => console.error('Failed to fetch favorites:', err));
  }, [userId, favRefresh]);

  // Close filter on outside click
  useEffect(() => {
    if (!showFilter) return;
    const handleClick = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilter(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showFilter]);

  // Toggle favorite (bookmark)
  const toggleFavorite = async (promptId, e) => {
    e.stopPropagation();
    const isFav = favoriteIds.has(promptId);
    // Optimistic update
    setFavoriteIds(prev => {
      const next = new Set(prev);
      if (isFav) next.delete(promptId); else next.add(promptId);
      return next;
    });
    if (isLocal) return;
    try {
      if (isFav) {
        await axios.delete('/lb-api/business-scenario/favorite', { data: { enterprise_id: userId, prompt_id: promptId }, withCredentials: true });
        setFavRefresh(prev => prev + 1);
      } else {
        await axios.post('/lb-api/business-scenario/favorite', { enterprise_id: userId, prompt_id: promptId }, { withCredentials: true });
        setFavRefresh(prev => prev + 1);
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
      // Revert on error
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (isFav) next.add(promptId); else next.delete(promptId);
        return next;
      });
    }
  };

  // Filter logic
  const allPrompts = (isLocal ? LOCAL_PROMPTS : prompts).filter(p => {
    const matchLabel = selectedLabels.length === 0 || selectedLabels.includes(p.label_id);
    const matchSearch = !searchQuery.trim() ||
      p.prompt_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.label_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchLabel && matchSearch;
  });

  const filteredPrompts = showFavorites
    ? allPrompts.filter(p => favoriteIds.has(p.id))
    : allPrompts;

  const hasActiveFilter = selectedLabels.length > 0 || showFavorites;
  const cardsPerPage = expandAll ? filteredPrompts.length : CARDS_PER_PAGE;
  const totalPages = Math.ceil(filteredPrompts.length / cardsPerPage);
  const visiblePrompts = filteredPrompts.slice(currentPage * cardsPerPage, currentPage * cardsPerPage + cardsPerPage);

  const handleScenarioClick = (scenario) => { setSelectedScenario(scenario); setModalOpen(true); };

  const handleModalSubmit = (data) => {
    setModalOpen(false);
    navigate('/business-conversation', {
      state: {
        language: data.language || language, proficiencyLevel,
        businessScenario: data.scenario?.prompt_title || '',
        businessCategory: data.scenario?.label_name || '',
        scenarioId: data.scenario?.scenario_id || '',
        avatar: data.avatar, dialect: data.dialect
      }
    });
  };

  const getPaginationItems = () => {
    const items = [];
    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) items.push(i);
    } else {
      // Always 7 slots: [0] [1] [...] [middle] [...] [last-1] [last]
      items.push(0, 1, '...');
      const showMiddle = currentPage >= 2 && currentPage <= totalPages - 3;
      if (showMiddle) {
        items.push(currentPage);
      } else {
        items.push('...');
      }
      items.push('...', totalPages - 2, totalPages - 1);
    }
    // Remove duplicate page numbers
    const seen = new Set();
    return items.filter(item => {
      if (item === '...') return true;
      if (seen.has(item)) return false;
      seen.add(item);
      return true;
    });
  };

  return (
    <div className="business-scenario-page">
      <Header user={user} />
      <div className="nav-header-container">
        <SideNav />
        <PageHeader breadcrumbs={breadcrumbs} language={language} proficiencyLevel={proficiencyLevel}
          onLanguageChange={(lang) => setLanguage(lang)} onLevelChange={(level) => setProficiencyLevel(level)} disableLevelDropdown={true} />
      </div>

      <main className="business-scenario-content">
        <div className="bs-top-row">
          <div className="bs-search-bar">
            <svg className="bs-search-icon" width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="#808080" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input type="text" className="bs-search-input"
              placeholder="Search different business scenarios here or filter through categories"
              value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(0); }} />
            <div className="bs-filter-wrapper" ref={filterRef}>
              <button className="bs-filter-btn" aria-label="Filter" onClick={() => {
                if (!showFilter) { setPendingLabels([...selectedLabels]); setPendingFavorites(showFavorites); }
                setShowFilter(!showFilter);
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 6H21M7 12H17M10 18H14" stroke="#808080" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
              {showFilter && (
                <div className="bs-filter-dropdown">
                  {(() => {
                    const allPromptsUnfiltered = isLocal ? LOCAL_PROMPTS : prompts;
                    const pendingMatchCount = pendingLabels.length === 0
                      ? allPromptsUnfiltered.length
                      : allPromptsUnfiltered.filter(p => pendingLabels.includes(p.label_id)).length;
                    const pendingFavCount = pendingLabels.length === 0
                      ? favoriteIds.size
                      : allPromptsUnfiltered.filter(p => pendingLabels.includes(p.label_id) && favoriteIds.has(p.id)).length;
                    return (<>
                  <label className="bs-filter-item">
                    <input type="checkbox" checked={pendingLabels.length === 0 && !pendingFavorites} onChange={() => {
                      setPendingLabels([]); setPendingFavorites(false);
                    }} />
                    <span>All Scenario ({pendingMatchCount})</span>
                  </label>
                  <label className="bs-filter-item">
                    <input type="checkbox" checked={pendingFavorites} onChange={() => {
                      const newFav = !pendingFavorites;
                      setPendingFavorites(newFav);
                      if (newFav) setPendingLabels([]);
                    }} />
                    <span>My Favorites ({pendingFavCount})</span>
                  </label>
                  <div className="bs-filter-divider"></div>
                  <div className="bs-filter-section-title">Categories of Prompts</div>
                  {labels.map(label => (
                    <label key={label.id} className="bs-filter-item">
                      <input type="checkbox" checked={pendingLabels.includes(label.id)} onChange={() => {
                        setPendingLabels(prev => prev.includes(label.id) ? prev.filter(id => id !== label.id) : [...prev, label.id]);
                        setPendingFavorites(false);
                      }} />
                      <span>{label.label_name}</span>
                    </label>
                  ))}
                  <button className="bs-filter-apply" onClick={() => {
                    setSelectedLabels(pendingLabels);
                    setShowFavorites(pendingFavorites);
                    setExpandAll(true);
                    setCurrentPage(0); setShowFilter(false);
                  }}>
                    Apply Filters ({pendingLabels.length})
                  </button>
                    </>);
                  })()}
                </div>
              )}
            </div>
          </div>
          <div className="bs-my-conversations-wrapper">
            <button className="bs-my-conversations-btn">
              <span className="bs-conv-label">My Conversations</span>
              <span className="bs-conv-divider"></span>
              <span className="bs-conv-chevron">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            </button>
          </div>
        </div>

        <h1 className="bs-main-title">Explore a range of business scenarios and choose one to begin your practice journey</h1>

        <div className="bs-info-row">
          <span className="bs-count-label">{filteredPrompts.length}  Business Scenario Prompts</span>
          <span className="bs-explore-more" onClick={() => { setExpandAll(!expandAll); setCurrentPage(0); }}>{(expandAll || hasActiveFilter) ? 'Show Less' : 'Explore more Scenarios'}</span>
        </div>

        {/* Active filter chips */}
        {(selectedLabels.length > 0 || expandAll || showFavorites) && (
          <div className="bs-filter-chips">
            {expandAll && selectedLabels.length === 0 && !showFavorites && (
              <span className="bs-filter-chip">
                All Scenarios
                <button onClick={() => { setExpandAll(false); setCurrentPage(0); }}>&times;</button>
              </span>
            )}
            {showFavorites && (
              <span className="bs-filter-chip">
                My Favorites
                <button onClick={() => { setShowFavorites(false); setCurrentPage(0); }}>&times;</button>
              </span>
            )}
            {selectedLabels.map(id => {
              const label = labels.find(l => l.id === id);
              return label ? (
                <span key={id} className="bs-filter-chip">
                  {label.label_name}
                  <button onClick={() => { setSelectedLabels(prev => { const updated = prev.filter(x => x !== id); if (updated.length === 0) { setShowFavorites(false); } return updated; }); setCurrentPage(0); }}>&times;</button>
                </span>
              ) : null;
            })}
          </div>
        )}

        <div className={`bs-cards-container${(expandAll || hasActiveFilter) ? ' expanded' : ''}`}>
          {visiblePrompts.map((prompt) => (
            <div className="bs-card" key={prompt.id}>
              <div className="bs-card-inner" onClick={() => handleScenarioClick(prompt)} style={{cursor:"pointer"}}>
                <div className="bs-card-accent"></div>
                <button className={`bs-card-bookmark${favoriteIds.has(prompt.id) ? ' active' : ''}`}
                  onClick={(e) => toggleFavorite(prompt.id, e)} aria-label="Bookmark"
                  onMouseEnter={(e) => { if (!favoriteIds.has(prompt.id)) { const rect = e.currentTarget.getBoundingClientRect(); setTooltipPos({ top: rect.top - 50, left: rect.left + rect.width / 2 }); } }}
                  onMouseLeave={() => setTooltipPos(null)}>
                  <img src={favoriteIds.has(prompt.id) ? "/images/bookmarkedselected.png" : "/images/Bookmark.svg"} alt="Bookmark" width="18" height="18" />
                </button>
                <div className="bs-card-content">
                  <span className="bs-card-chip">{prompt.label_name}</span>
                  <p className="bs-card-title">{prompt.prompt_title}</p>
                </div>
                <button className="bs-card-link" onClick={() => handleScenarioClick(prompt)}>
                  <span className="bs-card-link-inner">Practice Scenario <img src="/images/Arrow.svg" alt="" width="16" height="16" /></span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="bs-pagination">
            <button className="bs-page-arrow" onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} aria-label="Previous page">
              <img src="/images/leftarrow.png" alt="Previous" width="18" height="18" />
            </button>
            <div className="bs-page-numbers">
              {getPaginationItems().map((item, idx) =>
                item === '...' ? (
                  <span key={`ellipsis-${idx}`} className="bs-page-ellipsis">...</span>
                ) : (
                  <button key={item} className={`bs-page-num${currentPage === item ? ' active' : ''}`} onClick={() => setCurrentPage(item)}>{item + 1}</button>
                )
              )}
            </div>
            <button className="bs-page-arrow" onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1} aria-label="Next page">
              <img src="/images/rightarrow.png" alt="Next" width="18" height="18" />
            </button>
          </div>
        )}

        <div className="bs-illustrations">
          <img src="/images/BSFooter1.svg" alt="" className="bs-illust-left" aria-hidden="true" />
          <img src="/images/BSFooter2.svg" alt="" className="bs-illust-right" aria-hidden="true" />
        </div>
      </main>
      <Footer />
      <BusinessScenarioModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onSubmit={handleModalSubmit} language={language} scenario={selectedScenario} />
      {tooltipPos && ReactDOM.createPortal(
        <div style={{position:'fixed',top:tooltipPos.top,left:tooltipPos.left,transform:'translateX(-50%)',zIndex:99999,pointerEvents:'none'}}>
          <img src="/images/TooltipSVG.svg" alt="Mark Favorite" style={{display:'block',width:'106px',height:'auto'}} />
        </div>,
        document.body
      )}
    </div>
  );
};

export default BusinessScenario;